// src/pages/PedidoNovo.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  TextField,
  Paper,
  Box,
  Button,
  IconButton,
  Divider,
  Alert, // Importado para exibir o erro
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";

import api from "../services/api"; // <<-- usa axios com interceptor
import Autocomplete from "@mui/material/Autocomplete";


export default function PedidoNovo() {
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [condicoesPagamento, setCondicoesPagamento] = useState([]);

  const [clienteId, setClienteId] = useState("");
  const [condicaoPagamento, setCondicaoPagamento] = useState("");
  const [observacao, setObservacao] = useState("");

  const [buscaProduto, setBuscaProduto] = useState("");
  const [produtosFiltrados, setProdutosFiltrados] = useState([]);

  const [itemProdutoId, setItemProdutoId] = useState("");
  const [itemQuantidade, setItemQuantidade] = useState(1);
  // itemPreco é o estado que guarda o preço, podendo ser o padrão ou o editado.
  const [itemPreco, setItemPreco] = useState(""); 
  const [itemFoto, setItemFoto] = useState("");
  const [itens, setItens] = useState([]);

  const [erroForm, setErroForm] = useState(null); // Estado para mensagens de erro

  // ============================================
  // CARREGAR DADOS INICIAIS
  // ============================================

  const carregarClientes = async () => {
    try {
      const resp = await api.get("/empresas/clientes");
      setClientes(resp.data);
    } catch (err) {
      console.error("Erro ao carregar clientes:", err);
    }
  };
  
  const carregarCondicoesPagamento = async () => {
    try {
      const resp = await api.get("/condicaopagamento");
      setCondicoesPagamento(resp.data);
    } catch (err) {
      console.error("Erro ao buscar condições:", err);
    }
  };

  useEffect(() => {
    carregarClientes();
    carregarCondicoesPagamento();
    
    // Lista de produtos mockada. DEVE ser substituída por chamada à API!
    setProdutos([
      { id: 1, nome: "Cimento 50kg", preco: 30.00, foto: "/imagens/cimento.jpg" },
      { id: 2, nome: "Areia Média 1m³", preco: 120.50, foto: "/imagens/areia.jpg" },
      { id: 3, nome: "Brita 1 1m³", preco: 140.00, foto: "/imagens/brita.jpg" },
      { id: 4, nome: "Bloco Estrutural 14x19x39", preco: 4.50, foto: null },
      { id: 5, nome: "Vergalhão 8mm", preco: 22.80, foto: "/imagens/vergalhao.jpg" },
    ]);
  }, []);

  // ============================================
  // LÓGICA DE PRODUTOS
  // ============================================

  // Filtra produtos conforme a busca
  useEffect(() => {
    if (buscaProduto.trim() === "") {
      setProdutosFiltrados([]);
      return;
    }

    const resultado = produtos.filter((p) =>
      p.nome.toLowerCase().includes(buscaProduto.toLowerCase())
    );

    setProdutosFiltrados(resultado);
  }, [buscaProduto, produtos]);


  // Ação ao clicar no produto na lista filtrada
  const selecionarProduto = (produto) => {
    setItemProdutoId(produto.id);
    // Preenche o campo itemPreco com o valor padrão do produto
    setItemPreco(produto.preco); 
    setItemFoto(produto.foto || "/imagens/sem-foto.png");

    setBuscaProduto(produto.nome);
    setProdutosFiltrados([]);
  };

  // ============================================
  // LÓGICA DE ITENS
  // ============================================

  const adicionarItem = () => {
    setErroForm(null); // Limpa erros

    if (!itemProdutoId) {
        setErroForm("Selecione um produto antes de adicionar.");
        return;
    }
    
    // Validação de Preço e Quantidade
    const quantidade = Number(itemQuantidade);
    const precoFinal = Number(itemPreco);

    if (quantidade <= 0) {
        setErroForm("A quantidade deve ser maior que zero.");
        return;
    }
    if (precoFinal <= 0 || isNaN(precoFinal)) {
        setErroForm("O preço deve ser válido e maior que zero.");
        return;
    }

    const produto = produtos.find((p) => p.id === Number(itemProdutoId));

    const novoItem = {
      id: Date.now(),
      produtoId: produto.id,
      nome: produto.nome,
      quantidade: quantidade,
      preco: precoFinal, // Usa o preço do estado (pode ter sido editado)
      total: precoFinal * quantidade,
      foto: produto.foto || "/imagens/sem-foto.png",
    };

    setItens([...itens, novoItem]);

    // Limpa campos para o próximo item
    setBuscaProduto("");
    setItemProdutoId("");
    setItemQuantidade(1);
    setItemPreco("");
    setItemFoto("");
  };

  const atualizarItem = (id, campo, valor) => {
    const novos = itens.map((it) => {
      if (it.id === id) {
        const novo = { ...it, [campo]: valor };
        // Garante que o total é recalculado
        novo.total = Number(novo.quantidade) * Number(novo.preco); 
        return novo;
      }
      return it;
    });

    setItens(novos);
  };

  const removerItem = (id) => {
    setItens(itens.filter((i) => i.id !== id));
  };

  const totalGeral = itens.reduce((s, i) => s + i.total, 0);

  // ============================================
  // SALVAR PEDIDO
  // ============================================

  const salvarPedido = async (transmitido) => {
    setErroForm(null); // Limpa erros

    if (itens.length === 0) {
        setErroForm("O pedido deve ter pelo menos um item.");
        return;
    }
    if (!clienteId) {
        setErroForm("Selecione um cliente para o pedido.");
        return;
    }
    
    try {
      const clienteCodNum = clienteId ? Number(clienteId) : null;
      const condicaoNum = condicaoPagamento ? Number(condicaoPagamento) : null;

      const pedido = {
        Cliente_Cod: clienteCodNum,
        Cliente_Nome: (clientes.find(c => (c.ID ?? c.id) == clienteCodNum) || {}).NOME || 
                    (clientes.find(c => (c.ID ?? c.id) == clienteCodNum) || {}).nome || "",
        Data: new Date().toISOString().split("T")[0], 
        Condicao_Pag: condicaoNum,
        Valor: Number(totalGeral) || 0,
        Vendedor: "ADMIN",
        Observacao: observacao || "",
        Transmitido: transmitido ? "S" : "N",
        Itens: itens.map((i, index) => ({
          Item: index + 1,
          Produto_Cod: Number(i.produtoId || i.Produto_Cod || i.produto_cod),
          Produto_Nome: i.nome || i.Produto_Nome || i.produto_nome,
          Quantidade: Number(i.quantidade),
          Preco: Number(i.preco),
          Total: Number(i.total)
        }))
      };

      const response = await api.post("/pedido", pedido);

      if (response.status !== 200 && response.status !== 201) {
        const errorMsg = response.data?.message || "Erro desconhecido ao salvar.";
        throw new Error(errorMsg);
      }

      const data = response.data;
      const numero = data.Numero || data.numero || data.numeroPedido;

      alert(`Pedido salvo com sucesso! Número: ${numero}`);
      navigate("/pedidos");

    } catch (error) {
      console.error("Erro ao salvar pedido:", error);
      setErroForm(`Falha ao salvar o pedido. ${error.message || ''}`); 
    }
  };

  const salvarRascunho = () => salvarPedido(false);
  const transmitirPedido = () => salvarPedido(true);

  // ============================================
  // RENDERIZAÇÃO
  // ============================================
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" mb={3}>
        Novo Pedido
      </Typography>

      {/* FEEDBACK DE ERRO */}
      {erroForm && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {erroForm}
        </Alert>
      )}

      {/* DADOS DO PEDIDO */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          Dados do Pedido
        </Typography>

        <Grid
          container
          spacing={2}
          direction="column"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Grid item xs={12} md={12}>
            <Autocomplete
              options={clientes}
              // Ajusta para usar propriedades dinâmicas caso o nome da chave mude
              getOptionLabel={(c) => `${c.ID || c.id} - ${c.NOME || c.nome}`} 
              onChange={(event, value) => {
                setClienteId(value ? (value.ID || value.id) : "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Cliente" fullWidth />
              )}
              filterOptions={(options, state) =>
                options.filter((opt) =>
                  `${opt.ID || opt.id} - ${opt.NOME || opt.nome}`
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase())
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              select
              label="Condição de Pagamento"
              SelectProps={{ native: true }}
              value={condicaoPagamento}
              onChange={(e) => setCondicaoPagamento(e.target.value)}
            >
              <option value=""></option>
              {condicoesPagamento.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.condicao}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Observações"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      ---

      {/* ITENS DO PEDIDO (Layout corrigido e ajustado) */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" mb={2}>
          Itens do Pedido
        </Typography>

        {/* Linha 1: BUSCAR PRODUTO (CORREÇÃO APLICADA AQUI: removido spacing para forçar 100%) */}
        <Grid container sx={{ mb: 2 }}> {/* <-- REMOVIDO spacing={2} E ADICIONADO mb: 2 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Buscar Produto"
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(e.target.value)}
              // O fullWidth já deve funcionar corretamente sem a interferência do spacing
            />

            {produtosFiltrados.length > 0 && (
              <Paper
                sx={{
                  mt: 1,
                  maxHeight: 250,
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  zIndex: 1000, 
                  position: 'absolute', 
                  width: 'calc(100% - 48px)' // Ocupa a largura total da Paper (100% - padding de 24px de cada lado)
                }}
              >
                {produtosFiltrados.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.2,
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                      "&:hover": { background: "#f5f5f5" },
                    }}
                    onClick={() => selecionarProduto(p)}
                  >
                    <img
                      src={p.foto || "/imagens/sem-foto.png"}
                      alt={p.nome}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 8,
                        objectFit: "cover",
                        marginRight: 12,
                        border: "1px solid #ccc",
                      }}
                    />

                    <Box>
                      <Typography>{p.nome}</Typography>
                      <Typography variant="body2" color="gray">
                        Preço: R$ {p.preco.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Linha 2: QUANTIDADE, PREÇO UNITÁRIO e ADICIONAR (Esta Grid continua com spacing={2}) */}
        <Grid container spacing={2}> 
            {/* QUANTIDADE (3/12 de largura em telas grandes) */}
            <Grid item xs={4} md={3}>
                <TextField
                fullWidth
                type="number"
                label="Qtd."
                value={itemQuantidade}
                onChange={(e) => setItemQuantidade(Number(e.target.value))}
                disabled={!itemProdutoId}
                />
            </Grid>

            {/* PREÇO UNITÁRIO (3/12 de largura em telas grandes) */}
            <Grid item xs={4} md={3}>
                <TextField
                fullWidth
                type="number"
                label="Preço Unitário"
                value={itemPreco} 
                onChange={(e) => setItemPreco(e.target.value)} 
                disabled={!itemProdutoId} 
                />
            </Grid>

            {/* ADICIONAR (6/12 de largura em telas grandes, ficando na frente) */}
            <Grid item xs={4} md={6} display="flex" alignItems="center">
                <Button
                fullWidth
                variant="contained"
                onClick={adicionarItem}
                sx={{ height: "100%" }}
                disabled={!itemProdutoId} 
                >
                Adicionar
                </Button>
            </Grid>
        </Grid>


        <Divider sx={{ my: 4 }} />

        {/* LISTA DE ITENS ADICIONADOS (Não alterado) */}
        {itens.length === 0 ? (
          <Typography color="gray">Nenhum item adicionado.</Typography>
        ) : (
          <Box>
            {itens.map((item) => (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: "10px",
                  mb: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <img
                      src={item.foto}
                      alt={item.nome}
                      style={{
                        width: "100%",
                        height: 80,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography>
                      <b>{item.nome}</b>
                    </Typography>
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Qtd"
                      value={item.quantidade}
                      onChange={(e) =>
                        atualizarItem(item.id, "quantidade", Number(e.target.value))
                      }
                    />
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Preço"
                      value={item.preco}
                      onChange={(e) =>
                        atualizarItem(item.id, "preco", Number(e.target.value))
                      }
                    />
                  </Grid>

                  <Grid item xs={4} md={2} textAlign="right">
                    <IconButton onClick={() => removerItem(item.id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Grid>
                </Grid>

                <Typography mt={1} textAlign="right">
                  <b>Total Item:</b> R$ {item.total.toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      ---

      {/* TOTAL E BOTÕES */}
      <Box mt={4} textAlign="right" sx={{ pb: 4 }}>
        <Typography variant="h6" mb={2}>
          Total do Pedido: <b>R$ {totalGeral.toFixed(2)}</b>
        </Typography>

        <Button
          variant="outlined"
          color="secondary"
          sx={{ mr: 2 }}
          onClick={() => navigate("/pedidos")}
        >
          Cancelar
        </Button>

        <Button variant="contained" sx={{ mr: 2 }} onClick={salvarRascunho}>
          Salvar Rascunho
        </Button>

        <Button variant="contained" color="success" onClick={transmitirPedido}>
          Transmitir Pedido
        </Button>
      </Box>
    </Container>
  );
}